// Project-owned topology. No catalogue mutation and no legacy default changes.
import { fullCellLayout, hasBypassDiodes, panelCellGroupsMatch } from "./panelTopology";
export const CELL_MAP_MISMATCH_ERROR = "Η αποθηκευμένη διάταξη κυψελών χρειάζεται ενημέρωση. Ανοίξτε τη ρύθμιση διάταξης και πατήστε «Εφαρμογή στο έργο».";
export const DETAILED_SHADING_VERSION = 1;
export const TOPOLOGIES = [
  { value: "full", label: "Ολόκληρες κυψέλες — τμήματα σε σειρά" },
  { value: "half_shared", label: "Half-cut — κοινή δίοδος για άνω και κάτω τμήμα" },
  { value: "half_independent", label: "Half-cut — ξεχωριστές δίοδοι για κάθε μισό" },
  { value: "third_twin5", label: "Twin third-cut — 5 στήλες, κοινές δίοδοι" },
  { value: "full_no_bypass", label: "Ολόκληρες κυψέλες — χωρίς διόδους παράκαμψης" },
  { value: "half_no_bypass", label: "Half-cut — δύο παράλληλα μισά χωρίς διόδους" },
];

export function suggestedCellCount(module, config = {}) {
  const chars = module?.characteristics || module || {};
  const half = config.panelOrientation?.moduleHalfCut !== false;
  const raw = Number(chars.N_s ?? chars.Cells_in_Series ?? chars.cells_in_series);
  const catalogCount = Number.isInteger(raw) && raw > 0 ? raw : null;
  // Catalogue entries use N_s inconsistently: some store equivalent series
  // cells (e.g. 72), others physical half cells (e.g. 144). Never blindly
  // double it. These common counts provide a project-only, visible assumption,
  // not a reinterpretation of the catalogue's electrical parameters.
  const seriesAssumption = half && [54, 60, 66, 72, 78].includes(catalogCount);
  const count = catalogCount ? catalogCount * (seriesAssumption ? 2 : 1) : (half ? 120 : 60);
  return { catalogCount, count, seriesAssumption };
}

export function suggestedTopology(module, config = {}) {
  const half = config.panelOrientation?.moduleHalfCut !== false;
  const { count } = suggestedCellCount(module, config);
  const columns = 6;
  // Keep the suggested total exact; unusual counts must be resolved explicitly
  // by the user instead of rounding up and inventing physical cells.
  const rows = count / columns;
  return { version: 1, layout: half ? "half_shared" : "full", sectionAxis: "columns", cellRows: rows,
    cellColumns: columns, sections: 3, diodeVoltage: 0.5,
    breakdownVoltage: -15, breakdownFactor: 0.002, breakdownExponent: 3,
    sourceModule: config.read_module || module?.name || "", assumption: true };
}

export function detailedAvailabilityReason(module, config = {}) {
  // Method selection is a project preference. Equipment and a valid cell map
  // are required later, when configuring the topology and preparing a run.
  if (config.mountType !== "fixed") return "Η λεπτομερής μέθοδος υποστηρίζει προς το παρόν μόνο σταθερά πάνελ. Οι trackers δεν υποστηρίζονται ακόμη.";
  if (config.pvLosses?.dc?.shadingMode === "percentage") return "Έχει επιλεγεί σκίαση ως σταθερό ποσοστό. Για τη λεπτομερή μέθοδο χρειάζεται υπολογισμός σκίασης από τη φυσική διάταξη.";
  return "";
}

export function topologyErrors(value) {
  if (!value || ![1, 2].includes(value.version)) return ["Η διάταξη του πάνελ δεν έχει οριστεί."];
  const errors = [];
  if (!TOPOLOGIES.some((t) => t.value === value.layout)) errors.push("Επίλεξε ηλεκτρική συνδεσμολογία.");
  const sectionAxis = value.sectionAxis || "columns";
  if (!["columns", "rows"].includes(sectionAxis)) errors.push("Επίλεξε κατεύθυνση ηλεκτρικών τμημάτων.");
  const integer = (key, min, max) => Number.isInteger(Number(value[key])) && Number(value[key]) >= min && Number(value[key]) <= max;
  if (!integer("cellRows", 2, 60)) errors.push("Οι σειρές κυψελών πρέπει να είναι ακέραιος από 2 έως 60.");
  if (!integer("cellColumns", 2, 24)) errors.push("Οι στήλες κυψελών πρέπει να είναι ακέραιος από 2 έως 24.");
  const bypass = hasBypassDiodes(value);
  const sectionCells = sectionAxis === "columns" ? Number(value.cellColumns) : Number(value.cellRows) / (fullCellLayout(value) ? 1 : 2);
  if (!bypass) {
    if (value.version !== 2 || Number(value.sections) !== 1 || sectionAxis !== "columns") errors.push("Για διάταξη χωρίς διόδους, επιλέξτε ξανά την αντίστοιχη συνδεσμολογία. Κάθε κλάδος είναι ενιαίος.");
  } else if (value.layout === "third_twin5") {
    if (value.version !== 2 || Number(value.cellColumns) !== 5 || Number(value.sections) !== 3 || sectionAxis !== "columns") errors.push("Το πρότυπο twin third-cut χρειάζεται 5 στήλες και 3 κατακόρυφες ζώνες με πλάτος 2–1–2 στήλες.");
  } else if (!integer("sections", 1, 12) || sectionCells % Number(value.sections)) errors.push("Οι κυψέλες πρέπει να χωρίζονται ακριβώς στα ηλεκτρικά τμήματα.");
  if (!fullCellLayout(value) && Number(value.cellRows) % 2) errors.push("Η διάταξη με δύο μισά χρειάζεται άρτιο αριθμό σειρών.");
  if (Number(value.cellRows) * Number(value.cellColumns) > 480) errors.push("Η πιλοτική μέθοδος υποστηρίζει έως 480 κυψέλες ανά πάνελ.");
  if (bypass && !(Number(value.diodeVoltage) >= 0.01 && Number(value.diodeVoltage) <= 2)) errors.push("Η πτώση τάσης διόδου πρέπει να είναι από 0,01 έως 2 V.");
  if (!(Number(value.breakdownVoltage) <= -2 && Number(value.breakdownVoltage) >= -50)) errors.push("Η τάση ανάστροφης διάσπασης πρέπει να είναι από −50 έως −2 V.");
  if (!(Number(value.breakdownFactor) >= 0 && Number(value.breakdownFactor) <= 0.1)) errors.push("Μη έγκυρος συντελεστής ανάστροφης διάσπασης.");
  if (!(Number(value.breakdownExponent) >= 1 && Number(value.breakdownExponent) <= 10)) errors.push("Μη έγκυρος εκθέτης ανάστροφης διάσπασης.");
  if ([...(bypass ? ["diodeVoltage"] : []), "breakdownVoltage", "breakdownFactor", "breakdownExponent"].some((key) => value[key] === "" || value[key] == null || !Number.isFinite(Number(value[key])))) errors.push("Συμπλήρωσε όλες τις ηλεκτρικές παραμέτρους με έγκυρες τιμές.");
  if (!panelCellGroupsMatch(value)) errors.push(CELL_MAP_MISMATCH_ERROR);
  return errors;
}

export function diodeCount(topology) {
  if (!hasBypassDiodes(topology)) return 0;
  return Number(topology.sections) * (topology.layout === "half_independent" ? 2 : 1);
}

export function detailedSceneKey(config, dimensions) {
  // Added to the shared scene key by the caller; excludes generated geometry.
  return JSON.stringify([config.read_module, dimensions, config.pvDetailedShading?.topology,
    config.panelOrientation?.moduleOrientation]);
}

export function detailedShadingEnabled(config) {
  return config?.pvDetailedShading?.enabled === true;
}

export function detailedSceneInputs(config) {
  // A run must reject a stored mesh after the project geometry/wiring changes.
  // Keep this raw-input snapshot in sync with pv_core.detailed_config.scene_inputs.
  const pick = (object, keys) => keys.map((key) => object?.[key] ?? null);
  const saved = config.pv_design || {};
  return JSON.parse(JSON.stringify([
    pick(config, ["read_module", "mountType", "axisOrientation", "maxAngle", "backTrack", "loc_info", "panelOrientation", "systemDesign"]),
    saved.spacingInfo || null, saved.obstacleShading?.placement || null,
    (config.orientations || []).map((plane) => [pick(plane, ["id", "tilt", "azim", "tiltReference", "roofPitch", "roofAzimuth", "groundClearanceM", "parcel", "spacingInfo"]), plane.obstacleShading?.placement || null]),
    config.pvDetailedShading?.topology || null,
  ]));
}
